import Echo from 'laravel-echo';
import io from 'socket.io-client';
import { has } from 'lodash';

import { store } from './store';
import { Media } from 'ca-api-client';

import {
    stakeholderImportReport,
    generateHtmlReportSummary,
} from '@/components/stakeholder-import/stakeholderImportReport';

/**
 * Echo
 */
if (window.ca.echo && window.ca.echo.enabled) {
    console.info('echo - booting');
    window.eco = new Echo({
        broadcaster: 'socket.io',
        host: ca.echo.host + ':' + ca.echo.port,
        auth: {
            headers: {
                Authorization: 'Bearer ' + ca.user.token,
            },
        },
        client: io,
    });

    /**
     * Including the socket ID in API requests enables broadcasts that
     * exclude the originating client, or that targets them
     */
    eco.connector.socket.on('connect', function () {
        window.ca.echo.socketId = eco.socketId();
        window.axios.defaults.headers.common['X-Socket-Id'] = eco.socketId();
        store.commit('setEchoError', false);
    });

    eco.connector.socket.on('connect_error', function (reason) {
        // try to reconnect
        eco.connector.socket.connect();
        store.commit('setEchoError', reason);
    });

    eco.connector.socket.on('disconnect', (reason) => {
        if (reason === 'io server disconnect') {
            // the disconnection was initiated by the server, you need to reconnect manually
            eco.connector.socket.connect();
        }
    });

    /**
     * Media Toast message setup
     */
    if (ca.hasOwnProperty('project')) {
        console.info('echo - adding project listeners');

        /**
         * Media Gallery channel logic - Private
         */

        eco.private('project-' + ca.project.uuid)
            .listen('.media.updated', (e) => {
                const media = new Media(e.media);

                if (has(media, 'user') && has(media.user, 'id') && media.user.id && media.user.id === ca.user.uuid) {
                    store.dispatch('replaceGalleryMediaItem', media).then(() => {
                        // if (hasMediaViewItem) {
                        //     store.dispatch('replaceMediaViewItem', media);
                        // }
                    });
                }
            })

            .listen('.media.processed', (e) => {
                const media = new Media(e.media);
                const router = App.$router;
                const isThumbnail = (media.file.thumb_id && media.type === 'image') || media.type === 'thumb';
                const userName =
                    has(media, 'last_upload_by') && has(media.last_upload_by, 'id') && media.last_upload_by.id
                        ? media.last_upload_by.id === ca.user.uuid
                            ? 'by you'
                            : `by ${media.last_upload_by.name}`
                        : 'via API';
                const action = media.files.data.length > 1 ? 'updated' : 'created';
                const message = `Media item ${media.title} ${action} ${userName}`;

                console.log('MEDIA PROCESSED', media);

                const mediaProcessedAlert = {
                    media,
                    router,
                    type: 'media',
                    message: message,
                    timeout: ca.config('toast.timeout'),
                    showClose: true,
                };

                // New media for user who uploaded, except thumbnails, and not in gallery view
                if (has(media, 'user') && has(media.user, 'id') && media.user.id && media.user.id === ca.user.uuid) {
                    store.dispatch('prependOrReplaceGalleryMediaItem', media).then(() => {
                        if (!store.getters.getMediaSelectModalShow && !isThumbnail) {
                            ca.notify(mediaProcessedAlert);
                        }
                    });
                } else {
                    // non user who uploaded, not thumbnails
                    store.dispatch('prependOrReplaceGalleryMediaItem', media).then(() => {
                        if (!isThumbnail) ca.notify(mediaProcessedAlert);
                    });
                }

                // Update media view current item with new version
                const mediaViewCurrentItemId = store.getters.getMediaViewCurrentItemId;
                if (mediaViewCurrentItemId === media.id) {
                    store.dispatch('replaceMediaViewItem', media);
                }

                // media select modal active update it's items
                const mediaSelectModalOpen = store.getters.getMediaSelectModalShow;
                if (
                    mediaSelectModalOpen &&
                    has(media, 'user') &&
                    has(media.user, 'id') &&
                    media.user.id &&
                    media.user.id === ca.user.uuid &&
                    !isThumbnail
                ) {
                    store.dispatch('prependOrReplaceMediaSelectItem', media).then(() => {
                        store.dispatch('addMediaSelectItem', {
                            id: media.id,
                            mode: 'multiple',
                            checked: true,
                        });
                        store.commit('setMediaSelectIsLoading', false);
                    });
                }
            })

            .listen('.media.deleted', (e) => {
                console.log('.media.deleted', e.media);
                const userName =
                    has(e, 'user') && e.user != null
                        ? e.user.id === ca.user.uuid
                            ? 'by you'
                            : 'by ' + e.user.name
                        : 'via API';
                const message = `Media item ${e.media.title} deleted ${userName}`;

                store.dispatch('removeGalleryMediaItem', e.media.id).then(() => {
                    // user is on a deleted media item
                    if (e.media.id === store.getters.getMediaViewCurrentItemId) {
                        App.$router.replace({ name: 'gallery' });
                    }

                    ca.notify({
                        type: 'success',
                        icon: 'trash',
                        message: message,
                        timeout: ca.config('toast.timeout'),
                    });
                });
            });

        /**
         * Media Share channle logic - Private
         */
        eco.private(`project-${ca.project.uuid}.user-${ca.user.id}`).listen('.share.processed', (broadcastEvent) => {
            switch (broadcastEvent.platform) {
                // MediaLink
                case 'link':
                    // ShareModal is open, just show copy field
                    if (store.getters['getMediaShareModalShow']) {
                        store.commit('setMediaShareLink', broadcastEvent.link.short_link);
                        store.commit('setMediaShareIsSending', false);
                    } else {
                        // Show toast message, with view link to open modal
                        ca.notify({
                            icon: 'link',
                            type: 'info',
                            message: 'Your download link is ready',
                            actions: [
                                {
                                    text: 'View',
                                    onClick: (toastEvent, toastObject) => {
                                        store
                                            .dispatch('showShareLinkModal', broadcastEvent.link.short_link)
                                            .then(() => {
                                                toastObject.goAway(0);
                                            });
                                    },
                                },
                            ],
                        });
                    }
                    break;

                // YouTube
                case 'youtube':
                    let message = `${broadcastEvent.metadata.title} successfully shared to youtube.`;
                    // ShareModal is open, just show mesage
                    if (store.getters['getMediaShareModalShow']) {
                        store.commit('setMediaShareMessage', message);
                    } else {
                        if (broadcastEvent.media.shares) {
                            broadcastEvent.media.shares.forEach((share) => {
                                if (share.platform == 'youtube') {
                                    store.commit('setYoutubeShareId', share.share_id),
                                        then(() => {
                                            // Show toast message
                                            ca.notify({
                                                message,
                                                type: 'info',
                                                icon: 'photo-video',
                                                timeout: ca.config('toast.timeout'),
                                                showClose,
                                            });
                                        });
                                }
                            });
                        }
                    }
                    break;
            } // end switch
        });

        /**
         * Stakeholder import channel
         */
        eco.private('project-' + ca.project.uuid).listen('.stakeholder.processed', (e) => {
            ca.notify({
                type: 'success',
                icon: 'file-import',
                message: 'Stakeholder import successfully completed',
                actions: [
                    {
                        text: 'View report',
                        onClick: (toastEvent, toastObject) => {
                            toastObject.goAway(0);

                            const report = stakeholderImportReport(e.result);
                            console.log('report', report, e.result);
                            const message = document.createElement('p');
                            message.appendChild(document.createTextNode('Below is a summary of the result:'));
                            const html = `${message.outerHTML}${generateHtmlReportSummary(report).outerHTML}`;

                            ca.confirm({
                                html,
                                type: 'success',
                                title: 'Your import was successfully processed.',
                                showConfirmButton: false,
                                cancelButtonText: '<i class="fas fa-times"></i> Close',
                            });
                        },
                    },
                ],
                showClose: true,
            });
        });
    }

    /**
     * Export channel logic - Private
     */
    if (ca.hasOwnProperty('user')) {
        eco.private('export.' + ca.user.id).listen('.BroadcastExport', (e) => {
            if (typeof window.Storage !== 'undefined') {
                window.localStorage.setItem('export', JSON.stringify(e.payload));
            }

            const exportBtn = $('.js-export-btn');

            switch (e.payload.type) {
                case 'started':
                    const spinner = $('<i class="fas fa-spinner fa-spin"></i> <span>Exporting, please wait...</span>');
                    exportBtn.html(spinner);
                    break;
                case 'finished':
                    exportBtn.html('Export <span class="caret"></span>').prop('disabled', false);

                    const downloadText = `Begin download`; // <small>(${formatBytes(filesize)})</small>
                    ca.confirm({
                        type: 'download',
                        html: `<p>Click the button below to begin your download.<p><a class="btn btn-primary btn-lg center-block" onclick="window.localStorage.removeItem('export'); Swal.closeModal();" href="${e.payload.filename}">${downloadText}</a>`,
                    });

                    break;
                case 'message':
                    ca.notify({
                        message: e.payload.message,
                        type: 'error',
                        showClose: true,
                    });
                    break;
            }
        });
    } else {
        console.warn('Failed to authenticate with echo - no user?', ca);
    }
}
